// Mock data utilities for super admin testing
// Define our own interfaces to match the mock data shape
import type { BusinessReview, BusinessReviewStats, CharityReview, CharityReviewStats } from '../services/review.service';
import type { BusinessUser, Invitation } from '../types/userManagement';

// Extended business interface that matches our mock data structure
interface BusinessMock {
  id: string;
  business_name: string;
  description: string;
  phone_number: string;
  address?: string;
  parish?: string;
  business_type: string;
  website?: string;
  email: string;
  logo?: string;
  rating?: number;
  created_at: string;
  updated_at: string;
  status: string;
  [key: string]: any;
}

// Define a charity profile if not already defined in your types
interface CharityProfile {
  id: string;
  name: string;
  description: string;
  email: string;
  phone: string;
  address: string;
  parish?: string;
  website?: string;
  logo?: string;
  mission_statement?: string;
  tax_id?: string;
  founded_date?: string;
  status: string;
  created_at: string;
  updated_at: string;
  [key: string]: any;
}

// Sample business profiles for testing
export const mockBusinesses: BusinessMock[] = [
  {
    id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001',  // UUID format to match Supabase
    business_name: 'Island Fresh Seafood',
    description: 'Premium quality seafood sourced directly from local fishermen.',
    phone_number: '+1 (876) 555-1234',
    address: '123 Harbour Street, Kingston',
    parish: 'Kingston',
    business_type: 'other', // Changed from 'Distributor' to match available types
    website: 'www.islandfreshseafood.com',
    email: 'contact@islandfreshseafood.com',
    logo: 'https://placehold.co/400x300?text=Island+Fresh',
    rating: 4.8,
    created_at: '2024-11-15T08:00:00Z',
    updated_at: '2025-04-10T14:30:00Z',
    status: 'active'
  },
  {
    id: '7a1d08e8-f9b0-4cd8-a55a-49435583f002',  // UUID format to match Supabase
    business_name: 'Blue Mountain Catch',
    description: 'Sustainable fishing company specializing in mountain stream fish.',
    phone_number: '+1 (876) 555-2345',
    address: '45 Portland Avenue, Port Antonio',
    parish: 'Portland',
    business_type: 'other', // Changed from 'Producer' to match available types
    website: 'www.bluemountaincatch.com',
    email: 'info@bluemountaincatch.com',
    logo: 'https://placehold.co/400x300?text=Blue+Mountain',
    rating: 4.5,
    created_at: '2024-09-22T10:15:00Z',
    updated_at: '2025-03-18T09:45:00Z',
    status: 'active'
  },
  {
    id: '6b2c08e8-f9b0-4cd8-a55a-49435583f003',  // UUID format to match Supabase
    business_name: 'Caribbean Seafood Market',
    description: 'Local marketplace connecting fishermen with restaurants across Jamaica.',
    phone_number: '+1 (876) 555-3456',
    address: '789 Beach Road, Montego Bay',
    parish: 'St. James',
    business_type: 'supermarket', // Changed from 'Retailer' to match available types
    website: 'www.caribbeanseafoodmarket.com',
    email: 'sales@caribbeanseafoodmarket.com',
    logo: 'https://placehold.co/400x300?text=Caribbean+Market',
    rating: 4.2,
    created_at: '2025-01-05T16:20:00Z',
    updated_at: '2025-05-02T11:10:00Z',
    status: 'active'
  }
];

// Sample charity profiles for testing
export const mockCharities: CharityProfile[] = [
  {
    id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101',  // UUID format to match Supabase
    name: 'Jamaica Marine Conservation',
    description: 'Working to protect Jamaica\'s marine ecosystems and promote sustainable fishing practices.',
    email: 'contact@jamaicamarine.org',
    phone: '+1 (876) 555-7890',
    address: '42 Ocean View Road, Ocho Rios',
    parish: 'St. Ann',
    website: 'www.jamaicamarine.org',
    logo: 'https://placehold.co/400x300?text=Marine+Conservation',
    mission_statement: 'Protecting Jamaica\'s marine biodiversity for future generations',
    tax_id: '12-3456789',
    founded_date: '2010-06-15',
    status: 'active',
    created_at: '2024-10-10T09:30:00Z',
    updated_at: '2025-04-22T13:45:00Z'
  },
  {
    id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102',  // UUID format to match Supabase
    name: 'Feed the Coast',
    description: 'Providing nutritious seafood meals to underprivileged communities in coastal areas.',
    email: 'info@feedthecoast.org',
    phone: '+1 (876) 555-8901',
    address: '132 Harbor Street, Negril',
    parish: 'Westmoreland',
    website: 'www.feedthecoast.org',
    logo: 'https://placehold.co/400x300?text=Feed+the+Coast',
    mission_statement: 'Ensuring no coastal community goes hungry',
    tax_id: '23-4567890',
    founded_date: '2015-03-22',
    status: 'active',
    created_at: '2024-12-05T14:20:00Z',
    updated_at: '2025-05-10T10:15:00Z'
  },
  {
    id: '7f6f08e8-f9b0-4cd8-a55a-49435583f103',  // UUID format to match Supabase
    name: 'Fisher Folk Support Network',
    description: 'Providing education, resources and emergency assistance to fishing families.',
    email: 'support@fisherfolk.org',
    phone: '+1 (876) 555-9012',
    address: '56 Fisherman\'s Lane, Black River',
    parish: 'St. Elizabeth',
    website: 'www.fisherfolk.org',
    logo: 'https://placehold.co/400x300?text=Fisher+Folk',
    mission_statement: 'Supporting those who feed our nation from the sea',
    tax_id: '34-5678901',
    founded_date: '2012-11-18',
    status: 'active',
    created_at: '2024-08-30T11:10:00Z',
    updated_at: '2025-03-05T16:40:00Z'
  }
];

// Sample products for businesses
export const mockProducts = [
  {
    id: '5a7f08e8-f9b0-4cd8-a55a-49435583f201',  // UUID format to match Supabase
    business_id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001',
    name: 'Fresh Red Snapper',
    description: 'Fresh caught red snapper, perfect for grilling or baking.',
    price: 12.99,
    category: 'Fish',
    stock_quantity: 50,
    unit: 'lb',
    images: ['https://placehold.co/400x300?text=Red+Snapper'],
    created_at: '2025-04-01T08:30:00Z',
    updated_at: '2025-05-10T09:15:00Z'
  },
  {
    id: '4b8f08e8-f9b0-4cd8-a55a-49435583f202',  // UUID format to match Supabase
    business_id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001',
    name: 'Jumbo Shrimp',
    description: 'Large fresh shrimp sourced from local waters.',
    price: 15.99,
    category: 'Shellfish',
    stock_quantity: 35,
    unit: 'lb',
    images: ['https://placehold.co/400x300?text=Jumbo+Shrimp'],
    created_at: '2025-04-02T10:20:00Z',
    updated_at: '2025-05-09T14:40:00Z'
  },
  {
    id: '3c9f08e8-f9b0-4cd8-a55a-49435583f203',  // UUID format to match Supabase
    business_id: '7a1d08e8-f9b0-4cd8-a55a-49435583f002',
    name: 'Mountain Stream Tilapia',
    description: 'Freshwater tilapia raised in Blue Mountain streams.',
    price: 9.99,
    category: 'Fish',
    stock_quantity: 75,
    unit: 'lb',
    images: ['https://placehold.co/400x300?text=Mountain+Tilapia'],
    created_at: '2025-03-15T11:45:00Z',
    updated_at: '2025-05-08T08:30:00Z'
  }
];

// Sample donation requests for charities
export const mockDonationRequests = [
  {
    id: '2a0f08e8-f9b0-4cd8-a55a-49435583f301',  // UUID format to match Supabase
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101',
    title: 'Weekly Meal Program',
    description: 'Fish donations needed for our weekly meal program serving 100 families.',
    status: 'open',
    requested_items: [
      { type: 'Fish', quantity: 50, unit: 'lb' },
      { type: 'Shellfish', quantity: 20, unit: 'lb' }
    ],
    request_date: '2025-05-01T09:00:00Z',
    needed_by_date: '2025-05-15T09:00:00Z',
    created_at: '2025-05-01T09:00:00Z',
    updated_at: '2025-05-01T09:00:00Z'
  },
  {
    id: '1b1f08e8-f9b0-4cd8-a55a-49435583f302',  // UUID format to match Supabase
    charity_id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102',
    title: 'School Lunch Program',
    description: 'Seeking fish donations for nutrition program at five local schools.',
    status: 'open',
    requested_items: [
      { type: 'Fish', quantity: 100, unit: 'lb' },
      { type: 'Canned Fish', quantity: 50, unit: 'cans' }
    ],
    request_date: '2025-05-05T10:30:00Z',
    needed_by_date: '2025-05-20T10:30:00Z',
    created_at: '2025-05-05T10:30:00Z',
    updated_at: '2025-05-05T10:30:00Z'
  }
];

// Sample events for charities
export const mockEvents = [
  {
    id: '0c2f08e8-f9b0-4cd8-a55a-49435583f401',  // UUID format to match Supabase
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101',
    title: 'Coastal Cleanup Day',
    description: 'Join us for a day of cleaning up our beautiful coastline.',
    location: 'Hellshire Beach, St. Catherine',
    start_date: '2025-06-15T08:00:00Z',
    end_date: '2025-06-15T14:00:00Z',
    max_participants: 100,
    current_participants: 45,
    image: 'https://placehold.co/600x400?text=Coastal+Cleanup',
    status: 'upcoming',
    created_at: '2025-05-01T12:00:00Z',
    updated_at: '2025-05-10T15:30:00Z'
  },
  {
    id: 'fd3f08e8-f9b0-4cd8-a55a-49435583f402',  // UUID format to match Supabase
    charity_id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102',
    title: 'Community Fish Fry Fundraiser',
    description: 'Annual fundraiser with delicious seafood to support our feeding programs.',
    location: 'Negril Community Center',
    start_date: '2025-06-22T16:00:00Z',
    end_date: '2025-06-22T20:00:00Z',
    max_participants: 200,
    current_participants: 75,
    image: 'https://placehold.co/600x400?text=Fish+Fry',
    status: 'upcoming',
    created_at: '2025-05-05T09:15:00Z',
    updated_at: '2025-05-12T11:20:00Z'
  }
];

// Mock dashboard statistics for business
export const mockBusinessStats = {
  totalProducts: 24,
  totalOrders: 157,
  totalRevenue: 15780.45,
  totalDonations: 12,
  recentOrders: [
    {
      id: 'ec4f08e8-f9b0-4cd8-a55a-49435583f501',
      customer_name: 'Kingston Seafood Restaurant',
      date: '2025-05-12T14:30:00Z',
      total: 456.78,
      status: 'completed'
    },
    {
      id: 'dd5f08e8-f9b0-4cd8-a55a-49435583f502',
      customer_name: 'Blue Waters Hotel',
      date: '2025-05-11T10:15:00Z',
      total: 325.50,
      status: 'processing'
    }
  ],
  topProducts: [
    { name: 'Fresh Red Snapper', sales: 420.5 },
    { name: 'Jumbo Shrimp', sales: 385.75 },
    { name: 'Lobster Tail', sales: 320.25 }
  ],
  monthlySales: [
    { month: 'Jan', amount: 1250.45 },
    { month: 'Feb', amount: 1380.20 },
    { month: 'Mar', amount: 1425.65 },
    { month: 'Apr', amount: 1520.30 },
    { month: 'May', amount: 1480.55 }
  ]
};

// Mock review data for businesses
export const mockBusinessReviews: BusinessReview[] = [
  {
    id: 'b1r108e8-f9b0-4cd8-a55a-49435583e101',
    business_id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001', // Island Fresh Seafood
    user_id: 'u7e108e8-f9b0-4cd8-a55a-4943558aaa01',
    order_id: 'o1e108e8-f9b0-4cd8-a55a-4943aa83e101',
    rating: 5,
    timely_delivery: true,
    sufficient_quantity: true,
    easy_collection: true,
    comment: 'Excellent service! The seafood was extremely fresh and delivered ahead of schedule.',
    created_at: '2025-05-01T14:30:00Z'
  },
  {
    id: 'b2r208e8-f9b0-4cd8-a55a-49435583e102',
    business_id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001', // Island Fresh Seafood
    user_id: 'u8e208e8-f9b0-4cd8-a55a-4943558aaa02',
    order_id: 'o2e208e8-f9b0-4cd8-a55a-4943aa83e102',
    rating: 4,
    timely_delivery: true,
    sufficient_quantity: false,
    easy_collection: true,
    comment: 'Good quality but slightly less quantity than expected. Overall happy with the purchase.',
    created_at: '2025-05-05T10:15:00Z'
  },
  {
    id: 'b3r308e8-f9b0-4cd8-a55a-49435583e103',
    business_id: '7a1d08e8-f9b0-4cd8-a55a-49435583f002', // Blue Mountain Catch
    user_id: 'u9e308e8-f9b0-4cd8-a55a-4943558aaa03',
    order_id: 'o3e308e8-f9b0-4cd8-a55a-4943aa83e103',
    rating: 5,
    timely_delivery: true,
    sufficient_quantity: true,
    easy_collection: true,
    comment: 'Their mountain stream fish is incomparable! Will definitely order again.',
    created_at: '2025-05-08T16:45:00Z'
  },
  {
    id: 'b4r408e8-f9b0-4cd8-a55a-49435583e104',
    business_id: '6b2c08e8-f9b0-4cd8-a55a-49435583f003', // Caribbean Seafood Market
    user_id: 'u0e408e8-f9b0-4cd8-a55a-4943558aaa04',
    order_id: 'o4e408e8-f9b0-4cd8-a55a-4943aa83e104',
    rating: 3,
    timely_delivery: false,
    sufficient_quantity: true,
    easy_collection: false,
    comment: 'The quality was good but there were issues with delivery timing and collection.',
    created_at: '2025-05-10T09:20:00Z'
  }
];

// Mock business review statistics
export const mockBusinessReviewStats: Record<string, BusinessReviewStats> = {
  // Island Fresh Seafood stats
  '8c3e08e8-f9b0-4cd8-a55a-49435583f001': {
    average_rating: 4.7,
    total_reviews: 45,
    timely_delivery_percent: 95,
    sufficient_quantity_percent: 90,
    easy_collection_percent: 98,
    rating_distribution: {
      '1': 0,
      '2': 1,
      '3': 3,
      '4': 12,
      '5': 29
    },
    recent_comments: [
      {
        id: 'b1r108e8-f9b0-4cd8-a55a-49435583e101',
        comment: 'Excellent service! The seafood was extremely fresh and delivered ahead of schedule.',
        rating: 5,
        created_at: '2025-05-01T14:30:00Z'
      },
      {
        id: 'b2r208e8-f9b0-4cd8-a55a-49435583e102',
        comment: 'Good quality but slightly less quantity than expected. Overall happy with the purchase.',
        rating: 4,
        created_at: '2025-05-05T10:15:00Z'
      }
    ]
  },
  // Blue Mountain Catch stats
  '7a1d08e8-f9b0-4cd8-a55a-49435583f002': {
    average_rating: 4.5,
    total_reviews: 38,
    timely_delivery_percent: 92,
    sufficient_quantity_percent: 88,
    easy_collection_percent: 95,
    rating_distribution: {
      '1': 0,
      '2': 2,
      '3': 4,
      '4': 15,
      '5': 17
    },
    recent_comments: [
      {
        id: 'b3r308e8-f9b0-4cd8-a55a-49435583e103',
        comment: 'Their mountain stream fish is incomparable! Will definitely order again.',
        rating: 5,
        created_at: '2025-05-08T16:45:00Z'
      }
    ]
  },
  // Caribbean Seafood Market stats
  '6b2c08e8-f9b0-4cd8-a55a-49435583f003': {
    average_rating: 4.2,
    total_reviews: 32,
    timely_delivery_percent: 85,
    sufficient_quantity_percent: 92,
    easy_collection_percent: 80,
    rating_distribution: {
      '1': 1,
      '2': 2,
      '3': 5,
      '4': 14,
      '5': 10
    },
    recent_comments: [
      {
        id: 'b4r408e8-f9b0-4cd8-a55a-49435583e104',
        comment: 'The quality was good but there were issues with delivery timing and collection.',
        rating: 3,
        created_at: '2025-05-10T09:20:00Z'
      }
    ]
  }
};

// Mock review data for charities
export const mockCharityReviews: CharityReview[] = [
  {
    id: 'c1r108e8-f9b0-4cd8-a55a-49435583e201',
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101', // Jamaica Marine Conservation
    user_id: 'u7e108e8-f9b0-4cd8-a55a-4943558aaa01',
    donation_id: 'd1e108e8-f9b0-4cd8-a55a-4943aa83e201',
    rating: 5,
    timeliness: true,
    politeness: true,
    overall_satisfaction: true,
    comment: 'Incredible organization that truly makes a difference. Very professional and courteous.',
    created_at: '2025-05-02T15:30:00Z'
  },
  {
    id: 'c2r208e8-f9b0-4cd8-a55a-49435583e202',
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101', // Jamaica Marine Conservation
    user_id: 'u8e208e8-f9b0-4cd8-a55a-4943558aaa02',
    donation_id: 'd2e208e8-f9b0-4cd8-a55a-4943aa83e202',
    rating: 4,
    timeliness: true,
    politeness: true, 
    overall_satisfaction: false,
    comment: 'Good interaction with volunteers, but wished for more detailed feedback on how my donation was used.',
    created_at: '2025-05-06T11:15:00Z'
  },
  {
    id: 'c3r308e8-f9b0-4cd8-a55a-49435583e203',
    charity_id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102', // Feed the Coast
    user_id: 'u9e308e8-f9b0-4cd8-a55a-4943558aaa03',
    donation_id: 'd3e308e8-f9b0-4cd8-a55a-4943aa83e203',
    rating: 5,
    timeliness: true,
    politeness: true,
    overall_satisfaction: true,
    comment: 'Their commitment to feeding coastal communities is inspiring. Excellent communication throughout.',
    created_at: '2025-05-07T14:25:00Z'
  },
  {
    id: 'c4r408e8-f9b0-4cd8-a55a-49435583e204',
    charity_id: '7f6f08e8-f9b0-4cd8-a55a-49435583f103', // Fisher Folk Support Network
    user_id: 'u0e408e8-f9b0-4cd8-a55a-4943558aaa04',
    donation_id: 'd4e408e8-f9b0-4cd8-a55a-4943aa83e204',
    rating: 4,
    timeliness: false,
    politeness: true,
    overall_satisfaction: true,
    comment: 'Great charity with an important mission. There was a slight delay in communication but otherwise excellent.',
    created_at: '2025-05-11T10:20:00Z'
  }
];

// Mock charity review statistics
export const mockCharityReviewStats: Record<string, CharityReviewStats> = {
  // Jamaica Marine Conservation stats
  '9d4f08e8-f9b0-4cd8-a55a-49435583f101': {
    average_rating: 4.8,
    total_reviews: 42,
    timeliness_percent: 95,
    politeness_percent: 98,
    overall_satisfaction_percent: 92,
    rating_distribution: {
      '1': 0,
      '2': 0,
      '3': 2,
      '4': 6,
      '5': 34
    },
    recent_comments: [
      {
        id: 'c1r108e8-f9b0-4cd8-a55a-49435583e201',
        comment: 'Incredible organization that truly makes a difference. Very professional and courteous.',
        rating: 5,
        created_at: '2025-05-02T15:30:00Z'
      },
      {
        id: 'c2r208e8-f9b0-4cd8-a55a-49435583e202',
        comment: 'Good interaction with volunteers, but wished for more detailed feedback on how my donation was used.',
        rating: 4,
        created_at: '2025-05-06T11:15:00Z'
      }
    ]
  },
  // Feed the Coast stats
  '8e5f08e8-f9b0-4cd8-a55a-49435583f102': {
    average_rating: 4.7,
    total_reviews: 35,
    timeliness_percent: 94,
    politeness_percent: 97,
    overall_satisfaction_percent: 94,
    rating_distribution: {
      '1': 0,
      '2': 0,
      '3': 3,
      '4': 5,
      '5': 27
    },
    recent_comments: [
      {
        id: 'c3r308e8-f9b0-4cd8-a55a-49435583e203',
        comment: 'Their commitment to feeding coastal communities is inspiring. Excellent communication throughout.',
        rating: 5,
        created_at: '2025-05-07T14:25:00Z'
      }
    ]
  },
  // Fisher Folk Support Network stats
  '7f6f08e8-f9b0-4cd8-a55a-49435583f103': {
    average_rating: 4.5,
    total_reviews: 28,
    timeliness_percent: 85,
    politeness_percent: 96,
    overall_satisfaction_percent: 92,
    rating_distribution: {
      '1': 0,
      '2': 1,
      '3': 2,
      '4': 9,
      '5': 16
    },
    recent_comments: [
      {
        id: 'c4r408e8-f9b0-4cd8-a55a-49435583e204',
        comment: 'Great charity with an important mission. There was a slight delay in communication but otherwise excellent.',
        rating: 4,
        created_at: '2025-05-11T10:20:00Z'
      }
    ]
  }
};

// Mock data for Business Social Impact Dashboard
export const mockSocialImpactStatsBusiness = {
  totalDonations: 28,
  totalEvents: 12,
  totalVolunteers: 56,
  totalItems: 422,
  totalValueDonated: 8750.45,
  uniqueCharities: 5,
  impactMetrics: {
    environmental: 320,
    social: 280,
    economic: 150
  },
  recentActivities: [
    {
      id: 'act1-f9b0-4cd8-a55a-49435583f901',
      type: 'donation',
      title: 'Donation to Jamaica Marine Conservation',
      impact: 150,
      date: '2025-05-10T14:30:00Z'
    },
    {
      id: 'act2-f9b0-4cd8-a55a-49435583f902',
      type: 'event',
      title: 'Beach Cleanup Sponsorship',
      impact: 75,
      date: '2025-05-08T09:15:00Z'
    },
    {
      id: 'act3-f9b0-4cd8-a55a-49435583f903',
      type: 'donation',
      title: 'Donation to Fisher Folk Support Network',
      impact: 120,
      date: '2025-05-05T11:45:00Z'
    },
    {
      id: 'act4-f9b0-4cd8-a55a-49435583f904',
      type: 'volunteer',
      title: 'Staff Volunteer Day',
      impact: 45,
      date: '2025-05-01T10:00:00Z'
    },
    {
      id: 'act5-f9b0-4cd8-a55a-49435583f905',
      type: 'donation',
      title: 'Donation to Ocean Defenders',
      impact: 100,
      date: '2025-04-28T16:20:00Z'
    }
  ]
};

// Mock data for Charity Social Impact Dashboard
export const mockSocialImpactStatsCharity = {
  totalDonations: 42,
  totalEvents: 8,
  totalVolunteers: 87,
  impactMetrics: {
    environmental: 410,
    social: 350,
    economic: 180
  },
  recentActivities: [
    {
      id: 'cact1-f9b0-4cd8-a55a-49435583f911',
      type: 'donation',
      title: 'Donation from Island Fresh Seafood',
      impact: 180,
      date: '2025-05-12T15:30:00Z',
      business_name: 'Island Fresh Seafood'
    },
    {
      id: 'cact2-f9b0-4cd8-a55a-49435583f912',
      type: 'event',
      title: 'Coastal Cleanup Day',
      impact: 120,
      date: '2025-05-09T08:00:00Z'
    },
    {
      id: 'cact3-f9b0-4cd8-a55a-49435583f913',
      type: 'donation',
      title: 'Donation from Blue Mountain Catch',
      impact: 150,
      date: '2025-05-07T14:45:00Z',
      business_name: 'Blue Mountain Catch'
    },
    {
      id: 'cact4-f9b0-4cd8-a55a-49435583f914',
      type: 'event',
      title: 'Education Workshop',
      impact: 85,
      date: '2025-05-03T09:30:00Z'
    },
    {
      id: 'cact5-f9b0-4cd8-a55a-49435583f915',
      type: 'donation',
      title: 'Donation from Caribbean Seafood Market',
      impact: 130,
      date: '2025-04-29T11:20:00Z',
      business_name: 'Caribbean Seafood Market'
    }
  ]
};

// Mock data for Donation Requests (business view)
export const mockBusinessDonationRequests = [
  {
    id: 'breq1-f9b0-4cd8-a55a-49435583d101',
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101',
    charity_name: 'Jamaica Marine Conservation',
    product_id: '5a7f08e8-f9b0-4cd8-a55a-49435583f201',
    product_name: 'Fresh Red Snapper',
    quantity: 25,
    status: 'pending',
    created_at: '2025-05-14T09:30:00Z',
    updated_at: '2025-05-14T09:30:00Z'
  },
  {
    id: 'breq2-f9b0-4cd8-a55a-49435583d102',
    charity_id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102',
    charity_name: 'Ocean Defenders Jamaica',
    product_id: '6b7f08e8-f9b0-4cd8-a55a-49435583f203',
    product_name: 'Jumbo Shrimp',
    quantity: 15,
    status: 'accepted',
    created_at: '2025-05-12T14:15:00Z',
    updated_at: '2025-05-12T16:30:00Z'
  },
  {
    id: 'breq3-f9b0-4cd8-a55a-49435583d103',
    charity_id: '7f6f08e8-f9b0-4cd8-a55a-49435583f103',
    charity_name: 'Fisher Folk Support Network',
    product_id: '7c7f08e8-f9b0-4cd8-a55a-49435583f204',
    product_name: 'Fresh Tilapia',
    quantity: 30,
    status: 'rejected',
    created_at: '2025-05-10T11:45:00Z',
    updated_at: '2025-05-10T13:20:00Z'
  },
  {
    id: 'breq4-f9b0-4cd8-a55a-49435583d104',
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101',
    charity_name: 'Jamaica Marine Conservation',
    product_id: '7c7f08e8-f9b0-4cd8-a55a-49435583f204',
    product_name: 'Fresh Tilapia',
    quantity: 20,
    status: 'pending',
    created_at: '2025-05-14T15:00:00Z',
    updated_at: '2025-05-14T15:00:00Z'
  },
  {
    id: 'breq5-f9b0-4cd8-a55a-49435583d105',
    charity_id: '8e5f08e8-f9b0-4cd8-a55a-49435583f102',
    charity_name: 'Ocean Defenders Jamaica',
    product_id: '5a7f08e8-f9b0-4cd8-a55a-49435583f201',
    product_name: 'Fresh Red Snapper',
    quantity: 10,
    status: 'cancelled',
    created_at: '2025-05-08T10:00:00Z',
    updated_at: '2025-05-09T09:15:00Z'
  }
];

// Sample donation data (for business donations to charities)
export const mockBusinessDonations = [
  {
    id: 'bdon1-f9b0-4cd8-a55a-49435583e101',
    business_id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001', // Island Fresh Seafood
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101', // Jamaica Marine Conservation
    charity_name: 'Jamaica Marine Conservation',
    created_at: '2025-05-10T14:30:00Z',
    status: 'confirmed',
    environmental_impact: 120,
    social_impact: 90,
    economic_impact: 70,
    amount: 450,
    reviewed: true,
    items: [
      {
        id: 'item1-f9b0-4cd8-a55a-49435583g101',
        name: 'Fresh Red Snapper',
        quantity: 25,
        price: 12.99
      },
      {
        id: 'item2-f9b0-4cd8-a55a-49435583g102',
        name: 'Jumbo Shrimp',
        quantity: 10,
        price: 15.99
      }
    ]
  },
  {
    id: 'bdon2-f9b0-4cd8-a55a-49435583e102',
    business_id: '8c3e08e8-f9b0-4cd8-a55a-49435583f001', // Island Fresh Seafood
    charity_id: '7f6f08e8-f9b0-4cd8-a55a-49435583f103', // Fisher Folk Support Network
    charity_name: 'Fisher Folk Support Network',
    created_at: '2025-05-05T11:45:00Z',
    status: 'confirmed',
    environmental_impact: 80,
    social_impact: 110,
    economic_impact: 40,
    amount: 320,
    reviewed: false,
    items: [
      {
        id: 'item3-f9b0-4cd8-a55a-49435583g103',
        name: 'Fresh Tilapia',
        quantity: 20,
        price: 9.99
      },
      {
        id: 'item4-f9b0-4cd8-a55a-49435583g104',
        name: 'Fish Fillets',
        quantity: 15,
        price: 8.99
      }
    ]
  },
  {
    id: 'bdon3-f9b0-4cd8-a55a-49435583e103',
    business_id: '7a1d08e8-f9b0-4cd8-a55a-49435583f002', // Blue Mountain Catch
    charity_id: '9d4f08e8-f9b0-4cd8-a55a-49435583f101', // Jamaica Marine Conservation
    charity_name: 'Jamaica Marine Conservation',
    created_at: '2025-04-28T16:20:00Z',
    status: 'confirmed',
    environmental_impact: 90,
    social_impact: 60,
    economic_impact: 30,
    amount: 280,
    reviewed: true,
    items: [
      {
        id: 'item5-f9b0-4cd8-a55a-49435583g105',
        name: 'Mountain Trout',
        quantity: 15,
        price: 11.99
      },
      {
        id: 'item6-f9b0-4cd8-a55a-49435583g106',
        name: 'Crayfish',
        quantity: 10,
        price: 9.99
      }
    ]
  }
];

// Mock dashboard statistics for charity
export const mockCharityStats = {
  totalDonationsReceived: 87,
  totalEvents: 12,
  totalVolunteers: 45,
  totalPeopleFed: 2450,
  recentDonations: [
    {
      id: 'cd6f08e8-f9b0-4cd8-a55a-49435583f601',
      business_name: 'Island Fresh Seafood',
      date: '2025-05-10T09:45:00Z',
      items: [
        { name: 'Fresh Fish', quantity: 25, unit: 'lb' }
      ],
      status: 'received'
    },
    {
      id: 'be7f08e8-f9b0-4cd8-a55a-49435583f602',
      business_name: 'Blue Mountain Catch',
      date: '2025-05-08T14:20:00Z',
      items: [
        { name: 'Tilapia', quantity: 15, unit: 'lb' },
        { name: 'Shrimp', quantity: 10, unit: 'lb' }
      ],
      status: 'received'
    }
  ],
  upcomingEvents: [
    {
      id: '0c2f08e8-f9b0-4cd8-a55a-49435583f401',
      title: 'Coastal Cleanup Day',
      date: '2025-06-15T08:00:00Z',
      participants: 45
    },
    {
      id: 'fd3f08e8-f9b0-4cd8-a55a-49435583f402',
      title: 'Community Fish Fry Fundraiser',
      date: '2025-06-22T16:00:00Z',
      participants: 75
    }
  ],
  monthlyDonations: [
    { month: 'Jan', count: 15 },
    { month: 'Feb', count: 18 },
    { month: 'Mar', count: 12 },
    { month: 'Apr', count: 22 },
    { month: 'May', count: 20 }
  ]
};

/**
 * Utility function to get mock businesses with optional filtering
 */
export function getMockBusinesses(filters?: {
  parish?: string;
  business_type?: string;
  search?: string;
}) {
  let result = [...mockBusinesses];
  
  if (filters) {
    if (filters.parish) {
      result = result.filter(b => b.parish === filters.parish);
    }
    if (filters.business_type) {
      result = result.filter(b => b.business_type === filters.business_type);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(b => {
        // Safely access potentially missing properties
        const name = (b as any).business_name || b.name || '';
        const desc = (b as any).description || '';
        return name.toLowerCase().includes(searchLower) || 
               desc.toLowerCase().includes(searchLower);
      });
    }
  }
  
  return result;
}

/**
 * Utility function to get mock charities with optional filtering
 */
export function getMockCharities(filters?: {
  parish?: string;
  search?: string;
}) {
  let result = [...mockCharities];
  
  if (filters) {
    if (filters.parish) {
      result = result.filter(c => c.parish === filters.parish);
    }
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      result = result.filter(c => 
        c.name.toLowerCase().includes(searchLower) || 
        c.description.toLowerCase().includes(searchLower)
      );
    }
  }
  
  return result;
}

/**
 * Helper to intercept API calls and return mock data in development mode
 * This avoids interfering with authentication while still providing mock data
 */
export function setupMockDataInterceptor(api: any) {
  // Skip authentication-related requests to avoid interfering with real auth
  const shouldSkipInterception = (url: string) => {
    // Skip all auth-related endpoints
    if (url.includes('/auth/') || 
        url.includes('/rest/v1/profiles') || 
        url.includes('/rest/v1/business_staff') || 
        url.includes('supabase')) {
      return true;
    }
    return false;
  };
  
  // Request interceptor
  api.interceptors.request.use(
    async (config: any) => {
      // Skip interception for auth-related requests
      if (shouldSkipInterception(config.url)) {
        console.log('Skipping mock interception for auth request:', config.url);
        return config;
      }
      
      // For other requests in development mode, we can modify if needed
      console.log('Mock interceptor processing request:', config.url);
      return config;
    },
    (error: any) => Promise.reject(error)
  );
  
  // Response interceptor
  api.interceptors.response.use(
    (response: any) => {
      // Pass through successful responses
      return response;
    },
    async (error: any) => {
      // Skip interception for auth-related requests
      if (error.config && shouldSkipInterception(error.config.url)) {
        return Promise.reject(error);
      }
      
      // Only intercept 404 errors in development mode
      if (error.response?.status !== 404) {
        return Promise.reject(error);
      }
      
      const url = error.config.url;
      console.log('Intercepting 404 in development mode:', url);
      
      // Return mock data based on the URL
      if (url.includes('/api/business')) {
        return {
          data: mockBusinesses,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/charities')) {
        return {
          data: mockCharities,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/products')) {
        return {
          data: mockProducts,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/statistics/business/dashboard')) {
        return {
          data: mockBusinessStats,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/statistics/social-impact/business')) {
        return {
          data: mockSocialImpactStatsBusiness,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/statistics/social-impact/charity')) {
        return {
          data: mockSocialImpactStatsCharity,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/statistics/charity')) {
        return {
          data: mockCharityStats,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/donations/requests')) {
        return {
          data: mockDonationRequests,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/donation-requests')) {
        return {
          data: mockBusinessDonationRequests,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/events')) {
        return {
          data: mockEvents,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/donations')) {
        return {
          data: mockBusinessDonations,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/business-reviews')) {
        // Check if it's a stats request
        if (url.includes('/stats/')) {
          const businessId = url.split('/stats/')[1].split('?')[0];
          return {
            data: mockBusinessReviewStats[businessId] || mockBusinessReviewStats['8c3e08e8-f9b0-4cd8-a55a-49435583f001'],
            status: 200,
            statusText: 'OK (Mock Data)',
            headers: {},
            config: error.config
          };
        }
        // Otherwise return reviews
        const businessId = new URL(`http://example.com${url}`).searchParams.get('business_id');
        if (businessId) {
          return {
            data: mockBusinessReviews.filter(review => review.business_id === businessId),
            status: 200,
            statusText: 'OK (Mock Data)',
            headers: {},
            config: error.config
          };
        }
        return {
          data: mockBusinessReviews,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/api/charity-reviews')) {
        // Check if it's a stats request
        if (url.includes('/stats/')) {
          const charityId = url.split('/stats/')[1].split('?')[0];
          return {
            data: mockCharityReviewStats[charityId] || mockCharityReviewStats['9d4f08e8-f9b0-4cd8-a55a-49435583f101'],
            status: 200,
            statusText: 'OK (Mock Data)',
            headers: {},
            config: error.config
          };
        }
        // Otherwise return reviews
        const charityId = new URL(`http://example.com${url}`).searchParams.get('charity_id');
        if (charityId) {
          return {
            data: mockCharityReviews.filter(review => review.charity_id === charityId),
            status: 200,
            statusText: 'OK (Mock Data)',
            headers: {},
            config: error.config
          };
        }
        return {
          data: mockCharityReviews,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/business/staff')) {
        return {
          data: mockBusinessStaff,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/charity/staff')) {
        return {
          data: mockCharityStaff,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/business/invitations')) {
        return {
          data: mockBusinessInvitations,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      } else if (url.includes('/charity/invitations')) {
        return {
          data: mockCharityInvitations,
          status: 200,
          statusText: 'OK (Mock Data)',
          headers: {},
          config: error.config
        };
      }
      
      // If no mock data matches, proceed with the error
      return Promise.reject(error);
    }
  );
}

// Mock staff data for business
export const mockBusinessStaff: BusinessUser[] = [
  {
    id: 'staff1-f9b0-4cd8-a55a-49435583s101',
    name: 'John Smith',
    email: 'john.smith@islandfresh.com',
    role: 'owner',
    profilePicture: 'https://randomuser.me/api/portraits/men/1.jpg',
    status: 'active',
    createdAt: '2024-11-20T09:30:00Z',
    businessId: '8c3e08e8-f9b0-4cd8-a55a-49435583f001' // Island Fresh Seafood
  },
  {
    id: 'staff2-f9b0-4cd8-a55a-49435583s102',
    name: 'Sarah Johnson',
    email: 'sarah.johnson@islandfresh.com',
    role: 'manager',
    profilePicture: 'https://randomuser.me/api/portraits/women/2.jpg',
    status: 'active',
    createdAt: '2025-01-15T11:45:00Z',
    businessId: '8c3e08e8-f9b0-4cd8-a55a-49435583f001' // Island Fresh Seafood
  },
  {
    id: 'staff3-f9b0-4cd8-a55a-49435583s103',
    name: 'Michael Brown',
    email: 'michael.brown@islandfresh.com',
    role: 'supervisor',
    profilePicture: 'https://randomuser.me/api/portraits/men/3.jpg',
    status: 'active',
    createdAt: '2025-02-10T14:20:00Z',
    businessId: '8c3e08e8-f9b0-4cd8-a55a-49435583f001' // Island Fresh Seafood
  },
  {
    id: 'staff4-f9b0-4cd8-a55a-49435583s104',
    name: 'Emily Davis',
    email: 'emily.davis@islandfresh.com',
    role: 'staff',
    profilePicture: 'https://randomuser.me/api/portraits/women/4.jpg',
    status: 'active',
    createdAt: '2025-03-05T10:15:00Z',
    businessId: '8c3e08e8-f9b0-4cd8-a55a-49435583f001' // Island Fresh Seafood
  }
];

// Mock staff data for charity
export const mockCharityStaff: BusinessUser[] = [
  {
    id: 'staff5-f9b0-4cd8-a55a-49435583s201',
    name: 'Robert Wilson',
    email: 'robert.wilson@jamaicamarine.org',
    role: 'owner',
    profilePicture: 'https://randomuser.me/api/portraits/men/5.jpg',
    status: 'active',
    createdAt: '2024-10-15T08:45:00Z',
    businessId: '9d4f08e8-f9b0-4cd8-a55a-49435583f101' // Jamaica Marine Conservation
  },
  {
    id: 'staff6-f9b0-4cd8-a55a-49435583s202',
    name: 'Jessica Taylor',
    email: 'jessica.taylor@jamaicamarine.org',
    role: 'manager',
    profilePicture: 'https://randomuser.me/api/portraits/women/6.jpg',
    status: 'active',
    createdAt: '2024-12-20T13:30:00Z',
    businessId: '9d4f08e8-f9b0-4cd8-a55a-49435583f101' // Jamaica Marine Conservation
  },
  {
    id: 'staff7-f9b0-4cd8-a55a-49435583s203',
    name: 'David Martinez',
    email: 'david.martinez@jamaicamarine.org',
    role: 'staff',
    profilePicture: 'https://randomuser.me/api/portraits/men/7.jpg',
    status: 'active',
    createdAt: '2025-02-05T09:20:00Z',
    businessId: '9d4f08e8-f9b0-4cd8-a55a-49435583f101' // Jamaica Marine Conservation
  },
  {
    id: 'staff8-f9b0-4cd8-a55a-49435583s204',
    name: 'Amanda Lewis',
    email: 'amanda.lewis@jamaicamarine.org',
    role: 'volunteer',
    profilePicture: 'https://randomuser.me/api/portraits/women/8.jpg',
    status: 'active',
    createdAt: '2025-03-15T11:40:00Z',
    businessId: '9d4f08e8-f9b0-4cd8-a55a-49435583f101' // Jamaica Marine Conservation
  }
];

// Mock invitation data for business
export const mockBusinessInvitations: Invitation[] = [
  {
    id: 'inv1-f9b0-4cd8-a55a-49435583i101',
    email: 'pending.user1@islandfresh.com',
    role: 'staff',
    businessId: '8c3e08e8-f9b0-4cd8-a55a-49435583f001', // Island Fresh Seafood
    createdAt: '2025-05-10T14:30:00Z',
    expiresAt: '2025-05-17T14:30:00Z'
  },
  {
    id: 'inv2-f9b0-4cd8-a55a-49435583i102',
    email: 'pending.user2@islandfresh.com',
    role: 'supervisor',
    businessId: '8c3e08e8-f9b0-4cd8-a55a-49435583f001', // Island Fresh Seafood
    createdAt: '2025-05-12T10:15:00Z',
    expiresAt: '2025-05-19T10:15:00Z'
  }
];

// Mock invitation data for charity
export const mockCharityInvitations: Invitation[] = [
  {
    id: 'inv3-f9b0-4cd8-a55a-49435583i201',
    email: 'pending.user3@jamaicamarine.org',
    role: 'volunteer',
    businessId: '9d4f08e8-f9b0-4cd8-a55a-49435583f101', // Jamaica Marine Conservation
    createdAt: '2025-05-08T09:45:00Z',
    expiresAt: '2025-05-15T09:45:00Z'
  },
  {
    id: 'inv4-f9b0-4cd8-a55a-49435583i202',
    email: 'pending.user4@jamaicamarine.org',
    role: 'staff',
    businessId: '9d4f08e8-f9b0-4cd8-a55a-49435583f101', // Jamaica Marine Conservation
    createdAt: '2025-05-11T13:20:00Z',
    expiresAt: '2025-05-18T13:20:00Z'
  }
];

/**
 * Call this function in your main app initialization to set up mock data
 * Only used in development mode for testing purposes
 */
export function initMockData(api: any) {
  // Only initialize mock data in development mode
  if (process.env.NODE_ENV === 'development') {
    // Use a flag in localStorage to prevent multiple initializations
    const mockInitialized = localStorage.getItem('mock_data_initialized');
    if (!mockInitialized) {
      setupMockDataInterceptor(api);
      localStorage.setItem('mock_data_initialized', 'true');
      console.log('Mock data initialized for development mode');
    } else {
      console.log('Mock data already initialized for this session');
    }
  } else {
    console.log('Mock data initialization skipped - production mode');
  }
}
